import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SubscriptionGuard } from "@/components/SubscriptionGuard";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Empresas = lazy(() => import("./pages/Empresas"));
const Auditorias = lazy(() => import("./pages/Auditorias"));
const NaoConformidades = lazy(() => import("./pages/NaoConformidades"));
const MatrizRisco = lazy(() => import("./pages/MatrizRisco"));
const Legislacao = lazy(() => import("./pages/Legislacao"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SalaAuditor = lazy(() => import("./pages/SalaAuditor"));
const PlanoAcao = lazy(() => import("./pages/PlanoAcao"));
const Manual = lazy(() => import("./pages/Manual"));
const AdminUsuarios = lazy(() => import("./pages/AdminUsuarios"));
const DocumentosBPF = lazy(() => import("./pages/DocumentosBPF"));
const Backup = lazy(() => import("./pages/Backup"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const NotFound = lazy(() => import("./pages/NotFound"));

import { Loader2 } from "lucide-react";

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground animate-pulse font-medium">Carregando...</p>
    </div>
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <SidebarProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/manual" element={<Manual />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="/dashboard" element={<ProtectedRoute><SubscriptionGuard product="audits_bpf" productLabel="Audits_BPF" monthlyPrice={449}><Dashboard /></SubscriptionGuard></ProtectedRoute>} />
                <Route path="/sala-auditor" element={<ProtectedRoute><SubscriptionGuard product="audits_bpf" productLabel="Audits_BPF" monthlyPrice={449}><SalaAuditor /></SubscriptionGuard></ProtectedRoute>} />
                <Route path="/empresas" element={<ProtectedRoute><SubscriptionGuard product="audits_bpf" productLabel="Audits_BPF" monthlyPrice={449}><Empresas /></SubscriptionGuard></ProtectedRoute>} />
                <Route path="/auditorias" element={<ProtectedRoute><SubscriptionGuard product="audits_bpf" productLabel="Audits_BPF" monthlyPrice={449}><Auditorias /></SubscriptionGuard></ProtectedRoute>} />
                <Route path="/nao-conformidades" element={<ProtectedRoute><SubscriptionGuard product="audits_bpf" productLabel="Audits_BPF" monthlyPrice={449}><NaoConformidades /></SubscriptionGuard></ProtectedRoute>} />
                <Route path="/plano-acao" element={<ProtectedRoute><SubscriptionGuard product="audits_bpf" productLabel="Audits_BPF" monthlyPrice={449}><PlanoAcao /></SubscriptionGuard></ProtectedRoute>} />
                <Route path="/matriz-risco" element={<ProtectedRoute><SubscriptionGuard product="audits_bpf" productLabel="Audits_BPF" monthlyPrice={449}><MatrizRisco /></SubscriptionGuard></ProtectedRoute>} />
                <Route path="/legislacao" element={<ProtectedRoute><SubscriptionGuard product="audits_bpf" productLabel="Audits_BPF" monthlyPrice={449}><Legislacao /></SubscriptionGuard></ProtectedRoute>} />
                <Route path="/relatorios" element={<ProtectedRoute><SubscriptionGuard product="audits_bpf" productLabel="Audits_BPF" monthlyPrice={449}><Relatorios /></SubscriptionGuard></ProtectedRoute>} />
                <Route path="/documentos-bpf" element={<ProtectedRoute><SubscriptionGuard product="audits_bpf" productLabel="Audits_BPF" monthlyPrice={449}><DocumentosBPF /></SubscriptionGuard></ProtectedRoute>} />
                <Route path="/admin/usuarios" element={<ProtectedRoute><AdminUsuarios /></ProtectedRoute>} />
                <Route path="/backup" element={<ProtectedRoute><Backup /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </SidebarProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;